import classNames from "classnames";
import moment from "moment";
import Paywall from "components/shared/Paywall";
import styles from "rivals/components/pages/Article/Article.module.scss";
import Author from "rivals/components/pages/Article/Author";
import Blocks from "rivals/components/pages/Article/Blocks";
import type { Props } from "rivals/components/pages/Article/Body/types";
import ErrorBoundary from "rivals/components/pages/Article/ErrorBoundary";
import Headline from "rivals/components/pages/Article/Headline";
import InfiniteScroll from "rivals/components/pages/Article/InfiniteScroll";
import UrlUpdater from "rivals/components/pages/Article/UrlUpdater";
import { Chartbeat } from "rivals/components/shared/Chartbeat";
import {
  DATE_MMM_D_YYYY,
  DATETIME_YYYY_MM_DD_HH_MM_SS_Z,
  ROOT_SITE_NAME
} from "rivals/shared/constants";
import { BlockType } from "rivals/shared/interfaces/Content";
import { isNationalUmbrellaSite } from "rivals/shared/utils/nationals";

export default function Body({
  article: {
    body,
    canEdit,
    id,
    isPremium,
    isPreview,
    authorInfo,
    sport,
    section,
    publishedTime,
    title,
    topicTags,
    slug: articleSlug
  },
  site,
  giftingEnabled,
  index = 0,
  infiniteScrollMeta,
  printable = false,
  publisherAds = [],
  slug
}: Props): React.JSX.Element {
  const isInfiniteScrollEnabled = slug && infiniteScrollMeta;

  const blocks = printable
    ? body.filter(block => block.type !== BlockType.Ad)
    : body;

  const blocksClassNames = classNames(styles.blocks, {
    [styles.preview]: isPreview && body.length > 0
  });

  const publishedAtDate = moment(
    publishedTime,
    DATETIME_YYYY_MM_DD_HH_MM_SS_Z
  ).format(DATE_MMM_D_YYYY);

  const chartBeatAuthors = authorInfo?.publisherNameAndTitle;
  const chartBeatDomain =
    site.shortName === ROOT_SITE_NAME
      ? `${process.env.NEXT_PUBLIC_DOMAIN}`
      : `${site.subdomain}.${process.env.NEXT_PUBLIC_DOMAIN}`;

  let chartBeatSections = [
    "content",
    `content-${section}`,
    `contents-${sport}`,
    `content-${sport}-${section}`
  ].join(" ");

  if (site.id != authorInfo?.siteId) {
    chartBeatSections = `${chartBeatSections} cross-viewed`;
  }

  const premiumIconColor = isNationalUmbrellaSite(site?.shortName)
    ? undefined
    : `#${site.primaryColor}`;

  return (
    <div className={classNames({ [styles.notFirstArticle]: index > 0 })}>
      <UrlUpdater
        index={index}
        isInfiniteScrollEnabled={Boolean(isInfiniteScrollEnabled)}
        site={site}
        slug={articleSlug}
        topicTags={topicTags}
      >
        <ErrorBoundary withSuspense>
          <Headline
            canEdit={canEdit}
            contentId={id.toString()}
            headline={title}
            isPremium={isPremium}
            premiumIconColor={premiumIconColor}
            printable={printable}
            publishedAtDate={publishedAtDate}
          />
        </ErrorBoundary>
        {authorInfo && <Author authorInfo={authorInfo} />}
        <div className={blocksClassNames}>
          <Blocks
            blocks={blocks || body}
            contentSpaceId={site.contentSpaceId}
            index={index}
          />
        </div>
        {isPreview && (
          <Paywall
            isNational={isNationalUmbrellaSite(site.shortName)}
            primaryColor={site.primaryColor}
          />
        )}
      </UrlUpdater>
      <ErrorBoundary>
        <Chartbeat
          authors={chartBeatAuthors}
          domain={chartBeatDomain}
          sections={chartBeatSections}
          title={title}
        />
      </ErrorBoundary>
      {isInfiniteScrollEnabled && infiniteScrollMeta && (
        <InfiniteScroll
          giftingEnabled={giftingEnabled}
          index={index}
          infiniteScrollMeta={infiniteScrollMeta}
          publisherAds={publisherAds}
          site={site}
          slug={slug}
        />
      )}
    </div>
  );
}
